import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { LicenseModalPage } from './LicenseModal';
import { SimpleModal } from './SimpleModal';
import { useLocation } from 'wouter';

interface LicenseContextType {
  openRegistrationModal: () => void;
  closeRegistrationModal: () => void;
  isRegistrationModalOpen: boolean;
  isLicenseModalOpen: boolean;
  licenseData: LicenseData | null;
  setLicenseData: (data: LicenseData | null) => void;
  openLicenseModal: () => void;
  closeLicenseModal: () => void;
  email: string;
  setEmail: (email: string) => void;
}

interface LicenseProviderProps {
  children: ReactNode;
}

interface LicenseData {
  license: string;
  email: string;
  primaryUrl: string;
  backupUrl: string;
  updatedDate: string;
  timestamp?: string;
}

const LicenseContext = createContext<LicenseContextType | undefined>(undefined);

export function LicenseProvider({ children }: LicenseProviderProps) {
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [licenseData, setLicenseData] = useState<LicenseData | null>(null);
  const [email, setEmail] = useState('');
  const [location, setLocation] = useLocation();

  const openRegistrationModal = () => {
    setIsRegistrationModalOpen(true);
  };

  const closeRegistrationModal = () => {
    setIsRegistrationModalOpen(false);
  };

  const openLicenseModal = () => {
    setIsLicenseModalOpen(true);
    setLocation('/license');
  };

  const closeLicenseModal = () => {
    setIsLicenseModalOpen(false);
    if (location === '/license') {
      window.history.length > 1 ? window.history.back() : setLocation('/');
    } else {
      setLocation('/');
    }
  };

  // Open license modal if URL is /license
  useEffect(() => {
    if (location === '/license' && !isLicenseModalOpen) {
      setIsLicenseModalOpen(true);
    }
  }, [location, isLicenseModalOpen]);

  // Test mode: inject mock license data for /license?test=1 or localStorage flag (development only)
  useEffect(() => {
    if (
      !licenseData &&
      (window.location.search.includes('test=1') || localStorage.getItem('licenseTestMode'))
    ) {
      setLicenseData({
        license: 'TEST-1234-5678-ABCD',
        email: 'test@solanavolumebot.io',
        primaryUrl: 'https://example.com/bot.zip',
        backupUrl: 'https://example.com/bot.zip',
        updatedDate: new Date().toLocaleDateString(),
        timestamp: new Date().toISOString(),
      });
    }
  }, [licenseData]);

  return (
    <LicenseContext.Provider 
      value={{ 
        openRegistrationModal, 
        closeRegistrationModal, 
        isRegistrationModalOpen,
        isLicenseModalOpen,
        licenseData,
        setLicenseData,
        openLicenseModal,
        closeLicenseModal,
        email,
        setEmail
      }}
    >
      {/* If license dashboard is open, render it fullscreen and hide children */}
      {isLicenseModalOpen ? (
        <LicenseModalPage 
          licenseData={licenseData} 
          onClose={closeLicenseModal}
        />
      ) : (
        <>
          {children}
          <SimpleModal isOpen={isRegistrationModalOpen} onClose={closeRegistrationModal}>
            <iframe
              src="https://getlicense.solanavolumebot.io/register"
              title="Registration"
              width="100%"
              height="600"
              style={{ border: 'none', minHeight: '600px', width: '100%', borderRadius: 24 }}
              allow="clipboard-write"
            />
          </SimpleModal>
        </>
      )}
    </LicenseContext.Provider>
  );
}

export function useLicense() {
  const context = useContext(LicenseContext);
  
  if (context === undefined) {
    throw new Error('useLicense must be used within a LicenseProvider');
  }
  
  return context;
}